import { ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { ROLE_LABELS, type PublicPlayer, type RoomState } from "@ma-soi/game-core";
import { SeatAvatar } from "../SeatAvatar";
import { EliminationAccent } from "./PhaseArt";

function VoteResultCountdown({ room }: { room: RoomState }) {
  const endsAtMs = room.phaseEndsAtMs;
  const [remaining, setRemaining] = useState(() => endsAtMs ? Math.max(0, (endsAtMs - Date.now()) / 1000) : 0);

  useEffect(() => {
    if (!endsAtMs) return;
    const update = () => setRemaining(Math.max(0, (endsAtMs - Date.now()) / 1000));
    update();
    const interval = window.setInterval(update, 100);
    return () => window.clearInterval(interval);
  }, [endsAtMs]);

  const shown = room.paused ? Math.max(0, (room.pausedRemainingMs ?? 0) / 1000) : remaining;
  const progress = Math.min(1, shown / 5);
  const circumference = 2 * Math.PI * 42;
  return (
    <div className="vote-result-countdown" role="timer" aria-label={room.paused ? "Tạm dừng" : `Còn ${Math.ceil(shown)} giây`}>
      <svg viewBox="0 0 96 96" aria-hidden="true">
        <circle cx="48" cy="48" r="42" className="countdown-track" />
        <circle
          cx="48"
          cy="48"
          r="42"
          className="countdown-progress"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - progress)}
        />
      </svg>
      <strong>{Math.ceil(shown)}</strong>
      <span>{room.paused ? "Tạm dừng" : "giây"}</span>
    </div>
  );
}

export function VoteResultView({ room, players, striking = false }: { room: RoomState; players: PublicPlayer[]; striking?: boolean }) {
  const result = room.voteResult;
  if (!result) {
    return (
      <section className="surface-panel vote-result-panel vote-result-pending gothic-frame" aria-live="polite">
        <span className="phase-orbit" />
        <h2>Đang kiểm phiếu…</h2>
      </section>
    );
  }

  const target = result.targetId ? players.find((player) => player.uid === result.targetId) ?? null : null;
  const isTie = result.tied;
  const isNoVote = !target && !isTie;
  return (
    <div className="vote-result-stage">
      <section className={`surface-panel vote-result-panel gothic-frame ${target ? "eliminated" : isTie ? "no-elimination vote-tied" : "no-elimination vote-empty"}`} aria-live="assertive">
        <p className="cinematic-eyebrow">Kết quả bỏ phiếu</p>
        {target ? (
          <>
            <EliminationAccent active={striking} />
            <SeatAvatar seat={target.seat} avatarId={target.avatarId} name={target.name} muted />
            <h2>{target.name}</h2>
            <p className="vote-result-verdict">Đã bị dân làng loại</p>
            <span className="vote-count-chip">{result.maxVotes} phiếu</span>
            <span className="role-hidden-state">
              {target.revealedRole ? ROLE_LABELS[target.revealedRole] : <><ShieldCheck /> Vai vẫn được giữ kín</>}
            </span>
          </>
        ) : (
          <>
            <span className="vote-result-neutral-icon" aria-hidden="true"><img src="/assets/ui/gothic-v2/seal.svg" width="100" height="100" alt="" /></span>
            <h2>{isTie ? "Phiếu bầu hòa" : "Không có lá phiếu hợp lệ"}</h2>
            <p>{isTie ? "Không ai bị loại khỏi ngôi làng." : "Không ai bị loại trong hôm nay."}</p>
            {isTie && <span className="vote-count-chip">{result.maxVotes} phiếu cao nhất</span>}
          </>
        )}
        <VoteResultCountdown room={room} />
        <p className="vote-result-auto-note">
          {isNoVote || isTie ? "Đêm tiếp theo sẽ bắt đầu sau ít giây." : "Kết quả tiếp theo sẽ tự động diễn ra."}
        </p>
      </section>
    </div>
  );
}
